import type { History } from "@/types"

export type AgentPlaygroundContentState =
  | "evaluation"
  | "agents-loading"
  | "agents-load-error"
  | "agent-not-found"
  | "playground"
  | "loading"

export interface GetAgentPlaygroundContentStateParams {
  isEvaluationPage: boolean
  hasCurrentAgent: boolean
  isAgentsLoadError: boolean
  isAgentsLoaded: boolean
  canRenderPlayground: boolean
}

const COPILOT_INTRO_STORAGE_KEY = "agent-editor.copilot-intro-seen.v1"

type CopilotIntroductionAction = "open" | "dismiss"

interface CompleteCopilotIntroductionParams {
  action: CopilotIntroductionAction
  markSeen: () => void
  clearIntroduction: () => void
  openCopilot?: () => void
}

interface StartVersionTestParams {
  question?: string
  retryQuestion: (question: string) => void
  startNewTest: () => void
  selectHistory: (history?: History) => void
  onStarted?: () => void
}

interface VersionTestGuidanceScope {
  agentId: string
  version?: number
}

interface IsVersionTestGuidanceCurrentParams {
  guidance: VersionTestGuidanceScope | null
  currentAgentId?: string
  currentVersion?: number
}

export const hasSeenCopilotIntroduction = (
  getStorage: () => Pick<Storage, "getItem"> = () => window.localStorage
) => {
  try {
    return getStorage().getItem(COPILOT_INTRO_STORAGE_KEY) === "true"
  } catch {
    return false
  }
}

export const markCopilotIntroductionSeen = (
  getStorage: () => Pick<Storage, "setItem"> = () => window.localStorage
) => {
  try {
    getStorage().setItem(COPILOT_INTRO_STORAGE_KEY, "true")
  } catch {
    // Onboarding remains functional when storage is unavailable.
  }
}

export const completeCopilotIntroduction = ({
  action,
  markSeen,
  clearIntroduction,
  openCopilot,
}: CompleteCopilotIntroductionParams) => {
  markSeen()
  clearIntroduction()
  if (action === "open") openCopilot?.()
}

export const startVersionTest = ({
  question,
  retryQuestion,
  startNewTest,
  selectHistory,
  onStarted,
}: StartVersionTestParams) => {
  if (question) retryQuestion(question)
  else startNewTest()
  selectHistory(undefined)
  onStarted?.()
}

export const isVersionTestGuidanceCurrent = ({
  guidance,
  currentAgentId,
  currentVersion,
}: IsVersionTestGuidanceCurrentParams) => {
  if (!guidance || guidance.version === undefined) return false
  return (
    guidance.agentId === currentAgentId && guidance.version === currentVersion
  )
}

export const getAgentPlaygroundContentState = ({
  isEvaluationPage,
  hasCurrentAgent,
  isAgentsLoadError,
  isAgentsLoaded,
  canRenderPlayground,
}: GetAgentPlaygroundContentStateParams): AgentPlaygroundContentState => {
  if (isEvaluationPage) {
    if (hasCurrentAgent) return "evaluation"
    if (isAgentsLoadError) return "agents-load-error"
    if (isAgentsLoaded) return "agent-not-found"
    return "agents-loading"
  }

  if (canRenderPlayground) return "playground"
  return "loading"
}
